import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  DeviceType,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Label,
  ModalBody,
} from "@tandemdiabetes/sugarmate-react-components"

import { DurationInput } from "../DurationInput"
import { useEntryModalPrivateContext } from "../EntryModalPrivateContext"

import { SubEntryFooter } from "../SubEntryFooter"
import { SubEntryHeader } from "../SubEntryHeader"
import { useUpdateActivity } from "../useUpdateActivity"
import { DeviceTypeRadioButton } from "./DeviceTypeRadioButton"
import { usePolyglot } from "src/contexts"
import styles from "src/styles/entryModal.module.scss"
import type {
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

interface Props {
  currentSubEntry: EditableSubEntry<ActivityType.DeviceChange>;
  updateSubEntry: (s: Partial<EditableSubEntry<ActivityType.DeviceChange>>) => void;
  currentUserEntry: EditableUserEntry;
  setCurrentUserEntry: (userEntry: EditableUserEntry) => void;
}

const polyglotPrefix = "components.entry_modal.device_change."

export function DeviceChangeForm(props: Props) {
  const {
    currentSubEntry,
    updateSubEntry,
    currentUserEntry,
    setCurrentUserEntry,
  } = props

  const { device_change: activity } = currentSubEntry

  const updateActivity = useUpdateActivity(
    ActivityType.DeviceChange,
    activity,
    updateSubEntry,
  )

  const { readOnly } = useEntryModalPrivateContext()

  const polyglot = usePolyglot()

  const deviceTypeValue = activity?.device_type ?? DeviceType.NotProvided

  return (
    <>
      <SubEntryHeader
        activityType={ActivityType.DeviceChange}
        currentSubEntry={currentSubEntry}
        currentUserEntry={currentUserEntry}
        setCurrentUserEntry={setCurrentUserEntry}
        readOnly={readOnly}
        closeBtnId="device_change-entry-close-btn"
      />
      <ModalBody className={styles.modalBody}>
        <DurationInput
          currentSubEntry={currentSubEntry}
          updateSubEntry={updateSubEntry}
          readOnly={readOnly}
        />
        {readOnly ? (
          deviceTypeValue !== DeviceType.NotProvided && (
            <Label>
              {polyglot.t(
                `${polyglotPrefix}legend_with_device`,
                {
                  device: polyglot.t(`${polyglotPrefix}${deviceTypeValue}`),
                },
              )}
            </Label>
          )
        ) : (
          <DeviceTypeRadioButton
            activity={activity}
            updateActivity={updateActivity}
          />
        )}
        <SubEntryFooter
          currentSubEntry={currentSubEntry}
          currentUserEntry={currentUserEntry}
          updateSubEntry={
            updateSubEntry as (
              s: Partial<EditableSubEntry<SubEntryType>>
            ) => void
          }
          readOnly={readOnly}
          allowCreateWithoutChanges
        />
      </ModalBody>
    </>
  )
}
